import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'azure-blob-storage',
  version: '1.0.0',
  properties: [
    // === CREDENTIALS ===
    {
      key: 'credentialId',
      label: 'Azure Blob Storage Credentials',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'azure_blob_storage',
      },
      description: 'Azure Blob Storage account credentials',
    },

    // === RESOURCE SELECTOR ===
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'blob',
      noDataExpression: true,
      options: [
        { value: 'blob', name: 'Blob', description: 'Work with blobs (files)' },
        { value: 'container', name: 'Container', description: 'Work with containers' },
      ],
    },

    // === BLOB OPERATIONS ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getMany',
      noDataExpression: true,
      displayOptions: {
        show: {
          resource: ['blob'],
        },
      },
      options: [
        { value: 'copy', name: 'Copy', description: 'Copy a blob to another location' },
        { value: 'delete', name: 'Delete', description: 'Delete a blob' },
        { value: 'download', name: 'Download', description: 'Download a blob' },
        { value: 'get', name: 'Get', description: 'Get blob metadata' },
        { value: 'getMany', name: 'Get Many', description: 'List blobs in a container' },
        { value: 'upload', name: 'Upload', description: 'Upload a blob' },
      ],
    },

    // === CONTAINER OPERATIONS ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getMany',
      noDataExpression: true,
      displayOptions: {
        show: {
          resource: ['container'],
        },
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a new container' },
        { value: 'delete', name: 'Delete', description: 'Delete a container' },
        { value: 'get', name: 'Get', description: 'Get container metadata' },
        { value: 'getMany', name: 'Get Many', description: 'List all containers' },
      ],
    },

    // === CONTAINER NAME (for most operations) ===
    {
      key: 'containerName',
      label: 'Container Name',
      type: 'string',
      required: true,
      default: '',
      placeholder: 'my-container',
      description: 'The name of the container',
      displayOptions: {
        show: {
          resource: ['blob'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getContainers',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'containerName',
      label: 'Container Name',
      type: 'string',
      required: true,
      default: '',
      placeholder: 'my-container',
      description: 'The name of the container',
      displayOptions: {
        show: {
          resource: ['container'],
          operation: ['create', 'delete', 'get'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getContainers',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // === BLOB NAME (for blob operations) ===
    {
      key: 'blobName',
      label: 'Blob Name',
      type: 'string',
      required: true,
      default: '',
      placeholder: 'folder/file.txt',
      description: 'The name/path of the blob',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['copy', 'delete', 'download', 'get', 'upload'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getBlobs',
          dependsOn: ['containerName'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // === DOWNLOAD OPTIONS ===
    {
      key: 'decodeTextContent',
      label: 'Decode Text Content',
      type: 'boolean',
      default: true,
      description: 'Automatically decode text files (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text instead of just "contentBase64"',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['download'],
        },
      },
    },
    {
      key: 'parseJsonContent',
      label: 'Parse JSON Content',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON files into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['download'],
          decodeTextContent: [true],
        },
      },
    },

    // === UPLOAD OPTIONS ===
    {
      key: 'contentBase64',
      label: 'File Content (Base64)',
      type: 'text',
      required: true,
      default: '',
      placeholder: '{{ $node["Text to Binary"].output.binary.data.data }}',
      description: 'The file content encoded as Base64. Use an expression like {{ $node["Previous Node"].output.binary.data.data }} to get binary data from a previous node.',
      supportsExpressions: true,
      typeOptions: {
        rows: 4,
      },
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['upload'],
        },
      },
    },
    {
      key: 'contentType',
      label: 'Content Type',
      type: 'string',
      required: false,
      default: '',
      placeholder: 'application/octet-stream',
      description: 'The MIME type of the file (optional, auto-detected if not specified)',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['upload'],
        },
      },
    },
    {
      key: 'overwrite',
      label: 'Overwrite',
      type: 'boolean',
      default: true,
      description: 'Overwrite the blob if it already exists',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['upload'],
        },
      },
    },

    // === COPY OPTIONS ===
    {
      key: 'destinationContainer',
      label: 'Destination Container',
      type: 'string',
      required: false,
      default: '',
      placeholder: 'destination-container',
      description: 'The destination container (defaults to source container if not specified)',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['copy'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getContainers',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'destinationBlobName',
      label: 'Destination Blob Name',
      type: 'string',
      required: false,
      default: '',
      placeholder: 'folder/new-file.txt',
      description: 'The destination blob name (defaults to source name if not specified)',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['copy'],
        },
      },
    },

    // === DELETE OPTIONS ===
    {
      key: 'deleteSnapshots',
      label: 'Delete Snapshots',
      type: 'boolean',
      default: true,
      description: 'Also delete any snapshots of this blob',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['delete'],
        },
      },
    },

    // === LIST OPTIONS ===
    {
      key: 'prefix',
      label: 'Prefix',
      type: 'string',
      required: false,
      default: '',
      placeholder: 'folder/',
      description: 'Filter blobs by prefix (e.g., folder path)',
      displayOptions: {
        show: {
          resource: ['blob'],
          operation: ['getMany'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getFolders',
          dependsOn: ['containerName'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Folders',
      },
    },
    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      description: 'Return all results instead of limiting',
      displayOptions: {
        show: {
          operation: ['getMany'],
        },
      },
    },
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      description: 'Maximum number of results to return',
      displayOptions: {
        show: {
          operation: ['getMany'],
          returnAll: [false],
        },
      },
    },

    // === CONTAINER CREATE OPTIONS ===
    {
      key: 'accessLevel',
      label: 'Access Level',
      type: 'options',
      required: false,
      default: 'private',
      description: 'The public access level for the container',
      displayOptions: {
        show: {
          resource: ['container'],
          operation: ['create'],
        },
      },
      options: [
        { value: 'private', name: 'Private', description: 'No public access' },
        { value: 'blob', name: 'Blob', description: 'Public read access for blobs only' },
        { value: 'container', name: 'Container', description: 'Public read access for container and blobs' },
      ],
    },

    // === OUTPUT OPTIONS ===
    {
      key: 'simplifyOutput',
      label: 'Simplify Output',
      type: 'boolean',
      default: true,
      description: 'Return a simplified version of the response',
      displayOptions: {
        show: {
          operation: ['get', 'getMany'],
        },
      },
    },
  ],
};

export default schema;
